import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { formatDay } from '@/lib/tenantLeaseDetails';
import { projectStatusLabel, projectStatusColor, showsProjectStatus } from '@/lib/projectStatus';
import StatusPicker from '../project/StatusPicker';

/**
 * One project as a row: the same facts as the tile, on one line.
 *
 * The columns past the name are hidden on a narrow screen rather than wrapped
 * - a row that wraps to three lines is a worse tile, not a list.
 *
 * The date goes through formatDay for the reason given in CLAUDE.md: the API
 * returns a plain YYYY-MM-DD, and new Date() reads that as midnight UTC and
 * shows the day before for everyone at Westwood.
 *
 * tenant_in_default tints the row light orange, same as the tile in
 * ProjectCard - see the note there, and for `changeStatus`.
 */
export default function ProjectListRow({ project, percentComplete, changeStatus = false }) {
  const inDefault = !!project.tenant_in_default;
  return (
    <Link to={`/ProjectDetail?id=${project.id}`} className="block">
      <div
        className={`flex items-center gap-4 border rounded-lg px-4 py-2.5 text-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer group ${
          inDefault ? 'bg-orange-50 border-orange-300' : 'bg-card'
        }`}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-xs font-medium text-muted-foreground shrink-0">{project.project_number}</span>
            <span className="font-medium truncate group-hover:text-accent transition-colors">{project.name}</span>
          </div>
          {project.address && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">{project.address}</p>
          )}
        </div>

        <span className="hidden lg:block w-24 shrink-0 text-xs text-muted-foreground capitalize">
          {project.type ? project.type.replace(/_/g, ' ') : ''}
        </span>

        <span className="hidden md:block w-24 shrink-0 text-xs text-muted-foreground">
          {project.start_date ? formatDay(project.start_date) : ''}
        </span>

        <div className="hidden sm:flex items-center gap-2 w-28 shrink-0">
          <div className="h-1.5 flex-1 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-accent rounded-full transition-all duration-500"
              style={{ width: `${percentComplete}%` }}
            />
          </div>
          <span className="text-[10px] font-semibold tabular-nums w-8 text-right">{percentComplete}%</span>
        </div>

        {/* Fixed width from sm up, so the badge right-aligns in a column of its
            own. The badge is as wide as its words - "Attention Required" is
            three times "Revises" - and at the end of a flex row that
            difference shifts every column before it and the rows stop lining
            up. Below sm the width is dropped: 144px of a 375px phone screen
            spent on a badge truncates the project name to three letters, and
            there are no other columns left to align with anyway. */}
        <div className="shrink-0 flex flex-col items-end gap-1 sm:w-36">
          {changeStatus ? (
            <StatusPicker project={project} />
          ) : showsProjectStatus(project) && (
            <Badge className={`text-[10px] ${projectStatusColor(project.status)}`}>
              {projectStatusLabel(project.status)}
            </Badge>
          )}
          {inDefault && (
            <Badge
              className="text-[10px] bg-orange-200 text-orange-800 gap-1"
              title={project.tenant_default_reason || 'In default of lease commitments'}
            >
              <AlertTriangle className="w-2.5 h-2.5" /> In Default
            </Badge>
          )}
        </div>
      </div>
    </Link>
  );
}
